import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@bnsn/ui/lib/utils'

const buttonVariants = cva(
  // 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  'rounded inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-800',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-red-500',
        outline: 'border border-primary bg-transparent text-[#6e6e6e] hover:bg-primary-800 hover:text-white font-normal',
        'outline-dimmed':
          'border border-gray-500 bg-transparent text-gray-500 hover:bg-primary-800 hover:text-white font-normal',
        'outline-active': 'border border-primary text-white bg-primary',
        secondary: 'border border-secondary-foreground bg-secondary text-secondary-foreground hover:bg-secondary-300',
        accent: 'bg-accent border border-accent-foreground text-accent-foreground hover:bg-primary-300',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        icon: 'border border-primary bg-transparent text-[#6e6e6e] hover:bg-blue-bnsn hover:text-white rounded-full',

        iconSecondary:
          'bg-gray-200 text-action border border-gray-300 hover:bg-gray-200/80 hover:text-action rounded-md disabled:cursor-not-allowed',
        iconPrimary: 'bg-action text-primary-foreground hover:bg-action/90 rounded-md disabled:cursor-not-allowed',
        iconDestructive:
          'bg-background text-destructive border border-gray-200 hover:bg-gray-200 hover:action rounded-md disabled:cursor-not-allowed',
        iconOnly: 'text-black hover:text-black',
      },
      size: {
        default: 'h-11 px-6 py-2 text-sm',
        sm: 'h-10 rounded px-3 text-sm',
        lg: 'h-13 rounded px-8 text-lg',
        'icon-sm': 'h-8 w-8 text-sm',
        'icon-md': 'h-10 w-10 text-md [&_svg]:size-6',
        'icon-lg': 'h-12 w-12 text-lg [&_svg]:size-6',
        xsm: 'rounded p-2 text-sm leading-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
